import React, {
  useCallback,
  useEffect,
  useRef,
  type ComponentType,
} from "react";
import { Message } from "ai/react";
import { AskAnythingChatMessage } from "./AskAnythingChatMessage";
import styles from "./AskAnythingChat.module.scss";
import { ChainOfThoughtStep } from "@/components/ai-elements/chain-of-thought-for-improve-chat";
import { Text } from "@radix-ui/themes";
import improveChatLoader from "@/lottie/blood_cell_morph_loader.json";
import Lottie, { Options as LottieOptions, LottieProps } from "react-lottie";
import { useAskAnythingChat } from "./AskAnythingChatWrapper";
const LottieDisplay = Lottie as ComponentType<LottieProps>;

interface AskAnythingChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  loadingText?: string;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  variant?: "default" | "expanded";
}

export function AskAnythingChatMessages({
  messages,
  isLoading,
  loadingText = "Thinking...",
  scrollContainerRef,
  variant = "default",
}: AskAnythingChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(messages.length);
  const shouldAutoScrollRef = useRef(true);
  const { savedScrollPosition = null, saveScrollPosition = () => {} } =
    useAskAnythingChat();
  const hasRestoredScrollRef = useRef(false);

  const isNearBottom = useCallback(() => {
    const container = scrollContainerRef?.current;
    if (!container) {
      return true;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= 80;
  }, [scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      shouldAutoScrollRef.current = isNearBottom();
      // Save scroll position when in default (side) view
      if (variant === "default") {
        saveScrollPosition(container.scrollTop);
      }
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [isNearBottom, scrollContainerRef, variant, saveScrollPosition]);

  // Restore scroll position when opening expanded view
  useEffect(() => {
    if (
      variant === "expanded" &&
      savedScrollPosition !== null &&
      !hasRestoredScrollRef.current
    ) {
      const container = scrollContainerRef?.current;
      if (container) {
        let rafId: number | null = null;
        let retryCount = 0;
        const MAX_RETRIES = 50; // Prevent infinite loop

        // Wait for DOM to be ready and container to have proper dimensions
        const restoreScroll = () => {
          const currentContainer = scrollContainerRef?.current;
          if (!currentContainer) {
            return;
          }

          if (currentContainer.scrollHeight > 0) {
            currentContainer.scrollTop = savedScrollPosition;
            hasRestoredScrollRef.current = true;
          } else if (retryCount < MAX_RETRIES) {
            retryCount++;
            rafId = requestAnimationFrame(restoreScroll);
          }
        };
        // Use double requestAnimationFrame to ensure layout is complete
        rafId = requestAnimationFrame(() => {
          rafId = requestAnimationFrame(restoreScroll);
        });

        return () => {
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
          }
        };
      }
    } else if (variant === "default") {
      hasRestoredScrollRef.current = false;
    }
  }, [variant, savedScrollPosition, scrollContainerRef]);

  useEffect(() => {
    const hasNewMessage = messages.length > lastMessageCountRef.current;
    if (!hasNewMessage) {
      lastMessageCountRef.current = messages.length;
      return;
    }

    const lastMessage = messages[messages.length - 1];

    // Always scroll when user sends a message
    if (lastMessage?.role === "user") {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
      shouldAutoScrollRef.current = true;
      lastMessageCountRef.current = messages.length;
      return;
    }

    // Auto-scroll when receiving AI responses only if user is near bottom
    if (lastMessage?.role === "assistant") {
      if (shouldAutoScrollRef.current) {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
      lastMessageCountRef.current = messages.length;
      return;
    }

    // For any other message types, scroll if user is near bottom
    if (shouldAutoScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
    lastMessageCountRef.current = messages.length;
  }, [messages]);

  return (
    <div className={styles.messagesContainer}>
      <div className={styles.messagesList}>
        {messages.map((message, index) => (
          <AskAnythingChatMessage
            key={message.id}
            message={message}
            isLast={index === messages.length - 1 && !isLoading}
          />
        ))}
        {isLoading && (
          <ChainOfThoughtStep
            icon={
              <div className={styles.lottieWrapper}>
                <LottieDisplay
                  options={
                    {
                      loop: true,
                      autoplay: true,
                      animationData: improveChatLoader,
                    } as LottieOptions
                  }
                  height={26}
                  width={26}
                  isClickToPauseDisabled={true}
                />
              </div>
            }
            label=""
            status="active"
            variant="bubble"
            className={styles.chainOfThoughtStep}
          >
            <Text className={styles.loadingText}>{loadingText}</Text>
          </ChainOfThoughtStep>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
